import { MDXProvider } from '@mdx-js/react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { format } from 'date-fns'
import { ArrowLeft, Calendar } from 'lucide-react'
import { lazy, Suspense } from 'react'
import { MdxErrorBoundary } from '~/components/MdxErrorBoundary'
import { GradientBackground } from '~/components/backgrounds/GradientBackground'
import { SocialShare } from '~/components/blog/SocialShare'
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import { Badge } from '~/components/ui/badge'
import { getPostBySlug, getPostComponent, getRelatedPosts, type PostListItem } from '~/lib/blog'
import { mdxComponents } from '~/mdx-components'
import { seo } from '~/utils/seo'

export const Route = createFileRoute('/perspectives/$slug')({
  loader: async ({ params }) => {
    const entry = await getPostBySlug({ data: params.slug })
    const related = entry
      ? await getRelatedPosts({ data: { slug: params.slug, related: entry.frontmatter.relatedPosts } })
      : []
    // Only return serializable data from the loader
    const post = entry
      ? {
          slug: entry.slug,
          filePath: entry.filePath,
          frontmatter: entry.frontmatter,
        }
      : null
    return { post, related }
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post
    if (!post) return {}
    
    const ogImage = post.frontmatter.ogImage || '/OG.png'
    
    return {
      meta: seo({
        title: `${post.frontmatter.title} | CoPlane`,
        description: post.frontmatter.description,
        image: ogImage,
      }),
    }
  },
  component: BlogPostPage,
})

function BlogPostPage() {
  const { post, related } = Route.useLoaderData() as {
    post: PostListItem | null
    related: PostListItem[]
  }
  const { slug } = Route.useParams()

  if (!post) {
    return (
      <div className="flex min-h-screen items-center justify-center text-center">
        <div>
          <h1 className="font-sans mb-4 text-4xl">Post Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The blog post you&apos;re looking for doesn&apos;t exist or might
            have been moved.
          </p>
          <Link
            to="/perspectives"
            className="text-primary inline-flex items-center hover:underline"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Perspectives
          </Link>
        </div>
      </div>
    )
  }

  // Format date
  const formattedDate = format(new Date(post.frontmatter.date), "MMMM d, yyyy")

  const importer = getPostComponent(slug)
  if (!importer && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn(`[MDX] No importer found for slug "${slug}".`)
  }
  const Module = importer ? lazy(importer) : null

  return (
    <div className="relative min-h-screen w-full">
      {/* Background */}
      <div className="absolute inset-0 h-full w-full">
        <GradientBackground
          type="linear"
          position="to-br"
          stops={[
            { color: "#FFFFFF", position: 0 },
            { color: "#E5E7EB", position: 100 },
          ]}
          softness={150}
        />
      </div>

      <div className="relative pt-12 pb-24">
        <div className="mx-auto max-w-[100rem] sm:px-8 lg:px-12">
          {/* Back link */}
          <div className="mb-8 px-4 sm:px-0">
            <Link
              to="/perspectives"
              className="text-primary inline-flex items-center hover:underline"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to all perspectives
            </Link>
          </div>

          <div className="grid grid-cols-13 gap-8">
            {/* Sidebar */}
            <div className="order-2 col-span-13 transform-gpu lg:order-none lg:col-span-3">
              <div className="sticky top-32 space-y-10">
                {/* Author card */}
                {post.frontmatter.author && (
                  <div className="bg-card/50 border-foreground/20 rounded-lg rounded-bl-none border backdrop-blur-sm">
                    <div className="p-8">
                      <h3 className="text-foreground/70 mb-6 font-sans text-xs font-bold tracking-wider uppercase">
                        About the Author
                      </h3>
                      <div className="flex items-center gap-4">
                        <Avatar
                          className="h-14 w-14 shadow-sm"
                          variant="coplane-shape-1"
                          style={{
                            clipPath: "polygon(0 0, 100% 0, 90% 100%, 0% 100%)",
                          }}
                        >
                          <AvatarImage
                            src={post.frontmatter.author.avatarUrl}
                            alt={post.frontmatter.author.name}
                          />
                          <AvatarFallback className="bg-primary/5 text-primary font-medium">
                            {post.frontmatter.author.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="space-y-1">
                          <h4 className="font-sans text-sm leading-tight font-medium">
                            {post.frontmatter.author.name}
                          </h4>
                          <p className="text-muted-foreground text-xs leading-relaxed">
                            {post.frontmatter.author.title}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Related posts */}
                {related.length > 0 && (
                  <div className="bg-card/50 border-foreground/20 rounded-lg rounded-bl-none border backdrop-blur-sm">
                    <div className="p-8">
                      <h3 className="text-foreground/70 mb-6 font-sans text-xs font-bold tracking-wider uppercase">
                        Related Articles
                      </h3>
                      <div className="space-y-6">
                        {related.map((relatedPost) => (
                          <div key={relatedPost.slug} className="group">
                            <Link to="/perspectives/$slug" params={{ slug: relatedPost.slug }}>
                              <h4 className="font-sans group-hover:text-primary text-foreground text-base leading-snug font-medium transition-colors md:text-lg">
                                {relatedPost.frontmatter.title}
                              </h4>
                            </Link>
                            <p className="text-muted-foreground mt-2 line-clamp-2 text-sm leading-relaxed md:text-sm">
                              {relatedPost.frontmatter.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tags list */}
                {post.frontmatter.tags && post.frontmatter.tags.length > 0 && (
                  <div className="bg-card/50 border-foreground/20 rounded-lg rounded-bl-none border backdrop-blur-sm">
                    <div className="p-8">
                      <h3 className="text-foreground/70 mb-6 font-sans text-xs font-bold tracking-wider uppercase">
                        Topics
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {post.frontmatter.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" size="md">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Main content */}
            <div className="order-1 col-span-13 transform-gpu lg:order-none lg:col-span-10">
              <div className="bg-background/60 border-foreground/20 overflow-hidden rounded-lg rounded-br-none border backdrop-blur-md">
                <div className="p-8 md:p-12 lg:p-16">
                  {/* Header */}
                  <div className="mb-16">
                    {/* Tags */}
                    {post.frontmatter.tags &&
                      post.frontmatter.tags.length > 0 && (
                        <div className="mb-8 flex flex-wrap gap-2">
                          {post.frontmatter.tags.map((tag) => (
                            <Badge key={tag} variant="secondary" size="md">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}

                    {/* Date */}
                    <div className="text-muted-foreground mb-8 flex items-center text-sm">
                      <Calendar className="mr-2 h-4 w-4" />
                      <span>{formattedDate}</span>
                    </div>

                    {/* Title */}
                    <h1 className="font-sans mb-8 text-3xl leading-[1.2] tracking-tight [text-wrap:balance] md:text-4xl lg:text-5xl">
                      {post.frontmatter.title}
                    </h1>

                    {/* Cover Image */}
                    {post.frontmatter.cover && (
                      <div className="mb-8 md:mb-12 max-w-3xl">
                        <img
                          src={post.frontmatter.cover.src}
                          alt={post.frontmatter.cover.alt || post.frontmatter.title}
                          className="w-full rounded-lg border border-border"
                        />
                        {post.frontmatter.cover.caption && (
                          <p className="mt-2 text-center text-sm text-muted-foreground">
                            {post.frontmatter.cover.caption}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    <p className="text-muted-foreground mb-12 font-mono text-lg leading-relaxed [text-wrap:balance] md:text-xl">
                      {post.frontmatter.description}
                    </p>

                    {/* Meta information */}
                    <div className="border-foreground/20 flex items-start justify-between border-t pt-8">
                      {/* Author info left */}
                      {post.frontmatter.author && (
                        <div className="space-y-1">
                          <h4 className="font-sans text-sm leading-tight font-medium">
                            {post.frontmatter.author.name}
                          </h4>
                          <p className="text-muted-foreground text-xs leading-relaxed">
                            {post.frontmatter.author.title}
                          </p>
                        </div>
                      )}
                      {/* Social share for desktop */}
                      <div className="hidden items-center lg:flex">
                        <SocialShare
                          url={`https://coplane.com/perspectives/${post.slug}`}
                          title={post.frontmatter.title}
                          description={post.frontmatter.description}
                        />
                      </div>
                    </div>
                    {/* Social share for mobile */}
                    <div className="mt-6 block lg:hidden">
                      <SocialShare
                        url={`https://coplane.com/perspectives/${post.slug}`}
                        title={post.frontmatter.title}
                        description={post.frontmatter.description}
                      />
                    </div>
                  </div>

                  {/* MDX Content */}
                  {Module && (
                    <article className="prose prose-base dark:prose-invert [&_p]:text-foreground/90 [&_li]:text-foreground/90 [&_blockquote]:text-foreground/90 max-w-3xl">
                        <MDXProvider components={mdxComponents}>
                        <MdxErrorBoundary slug={slug} sourcePath={post.filePath}>
                            <Suspense fallback={null}>
                            <Module />
                            </Suspense>
                        </MdxErrorBoundary>
                        </MDXProvider>
                    </article>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
